/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { IPerformanceClient, Logger, AccountInfo } from "@azure/msal-browser";
import { IExtendedTelemetryItem } from "@microsoft/1ds-analytics-js";
import {
    PerformanceCallbackFunction,
    PerformanceEvent,
    PerformanceEvents,
    TelemetryQueueConfig,
} from "./TelemetryUtils.js";
import { PerformanceTelemetryReporter } from "./PerformanceTelemetryReporter.js";

export type PerformanceTelemetryEvent = IExtendedTelemetryItem["data"] & {
    name: string;
};
export type TelemetryReporterConfiguration = {
    /** When true, disables telemetry reporting entirely */
    disabled?: boolean;
    /** List of performance events to emit. Only events in the allowedEvents set will be included */
    eventsToEmit?: PerformanceEvents[];
    /** Application name to include in telemetry events for identification */
    appName?: string;
    /** Application version to include in telemetry events for tracking */
    appVersion?: string;
    /** When true, marks telemetry events as synthetic (e.g., for testing purposes) */
    synthetic?: boolean;
    /** Configuration for telemetry queue behavior (batching, flush intervals, etc.) */
    queueConfig?: TelemetryQueueConfig;
    /** Unique identifier for the telemetry profile, used for correlating events across sessions */
    profileTelemetryId?: string;
};

/**
 * Make sure to propagate new table name to GTAS rules https://identitydivision.visualstudio.com/IDDP/_git/msal-javascript-1p?path=/.GDPR/Policy/75ae2111-85e0-4898-922d-c242db4a92af/Kusto/Kusto_top1.tasp.json
 * when adding a new entity to miscEvents. Otherwise, it will be automatically marked as "Out of scope" in S360 data catalog inventory.
 */
// Sampled events for perf analysis
const miscEvents: PerformanceEvents[] = [
    PerformanceEvents.InitializeClientApplication,
    PerformanceEvents.LocalStorageUpdated,
];

/**
 * Make sure to propagate new table name to GTAS rules https://identitydivision.visualstudio.com/IDDP/_git/msal-javascript-1p?path=/.GDPR/Policy/75ae2111-85e0-4898-922d-c242db4a92af/Kusto/Kusto_top1.tasp.json
 * when adding a new entity to allowedEvents. Otherwise, it will be automatically marked as "Out of scope" in S360 data catalog inventory.
 */
const allowedEvents: Set<PerformanceEvents> = new Set([
    PerformanceEvents.AcquireTokenSilent,
    PerformanceEvents.AcquireTokenByCode,
    PerformanceEvents.AcquireTokenPopup,
    PerformanceEvents.AcquireTokenRedirect,
    PerformanceEvents.AcquireTokenPreRedirect,
    PerformanceEvents.PairwiseBrokerApplicationInitializeBrokering,
    PerformanceEvents.SsoSilent,
    PerformanceEvents.BackgroundSessionRefresh,
    // Embedded events
    PerformanceEvents.EmbeddedClientApplicationSendSSOSilentRequest,
    PerformanceEvents.EmbeddedClientApplicationSendPopupRequest,
    PerformanceEvents.EmbeddedClientApplicationSendRedirectRequest,
    PerformanceEvents.EmbeddedClientApplicationHandleRedirectRequest,
    PerformanceEvents.EmbeddedClientApplicationSendSilentRefreshRequest,
    PerformanceEvents.LoadExternalTokens,
    ...miscEvents,
]);

/**
 * Determines which data boundary (WW or EU) to route telemetry to based on account information.
 *
 * Priority order:
 * 1. If event has data boundary, use it
 * 2. If no data boundary exist, use active account's boundary
 * 3. If no active account exist, search for data boundary in all accounts. Resolve to "EU" if at least one account has "EU" data boundary.
 * 4. Default to WW otherwise
 *
 * @param event - Performance event that may contain account information
 * @param getActiveAccount - Function to get currently active account
 * @param getAllAccounts - Function to get all cached accounts
 * @param logger - Logger instance
 * @returns Data boundary ("WW" or "EU") for routing telemetry
 */
function determineDataBoundary(
    event: PerformanceEvent,
    getActiveAccount: () => AccountInfo | null,
    getAllAccounts: () => AccountInfo[],
    logger: Logger
): "EU" | "None" {
    if (event.dataBoundary) {
        return event.dataBoundary;
    }

    logger.trace(
        `TelemetryReporter. Account info is not present for event '${event.name}'`,
        event.correlationId
    );

    // No additional caching is required for account boundaries as account info is stored in memory since v4
    const activeAccount = getActiveAccount();
    if (activeAccount) {
        const boundary = activeAccount.dataBoundary || "None";
        logger.trace(
            `TelemetryReporter. Active account is found for '${event.name}'. Data boundary: '${boundary}'`,
            event.correlationId
        );
        return boundary;
    }

    const allAccounts = getAllAccounts();
    if (allAccounts.length > 0) {
        for (const account of allAccounts) {
            /*
             * Set data boundary to EU if telemetry event is not affiliated with an account
             * and there is at least one EU account in the cache. This ensures GDPR compliance
             * by treating multi-account scenarios conservatively.
             */
            if (account.dataBoundary === "EU") {
                logger.trace(
                    `TelemetryReporter. Found account with EU data boundary for '${event.name}'`,
                    event.correlationId
                );
                return "EU";
            }
        }
    }

    logger.trace(
        `TelemetryReporter. No accounts with EU data boundary found for '${event.name}'. Defaulting to WW`,
        event.correlationId
    );
    return "None";
}

export class TelemetryReporter {
    private perfTelemetryReporterWW: PerformanceTelemetryReporter;
    private perfTelemetryReporterEU: PerformanceTelemetryReporter;
    private logger: Logger;
    private readonly getActiveAccount: () => AccountInfo | null;
    private readonly getAllAccounts: () => AccountInfo[];

    /**
     * Creates TelemetryReporter with dual regional endpoints for data boundary compliance.
     * Initializes both WW (worldwide) and EU (European) telemetry reporters to route
     * telemetry data to appropriate regional endpoints based on account data boundaries.
     */
    constructor(
        clientId: string,
        logger: Logger,
        performanceClient: IPerformanceClient,
        channelId: string,
        getActiveAccount: () => AccountInfo | null,
        getAllAccounts: () => AccountInfo[],
        configuration?: TelemetryReporterConfiguration
    ) {
        this.logger = logger;

        const eventsToEmit: PerformanceEvents[] =
            configuration?.eventsToEmit?.filter((event) =>
                allowedEvents?.has(event)
            ) || [...allowedEvents];

        // Define telemetry endpoints configuration
        const telemetryEndpoints = {
            WW: "https://browser.events.data.microsoft.com/OneCollector/1.0/",
            EU: "https://eu-mobile.events.data.microsoft.com/OneCollector/1.0/",
        };

        // Helper function to create telemetry reporters
        const createTelemetryReporter = (
            endpoint: string
        ): PerformanceTelemetryReporter => {
            return new PerformanceTelemetryReporter(
                clientId,
                logger,
                performanceClient,
                eventsToEmit,
                miscEvents,
                channelId,
                endpoint,
                configuration?.synthetic,
                configuration?.appName,
                configuration?.appVersion,
                configuration?.queueConfig,
                configuration?.profileTelemetryId
            );
        };

        // Initialize telemetry reporters for different regions
        this.perfTelemetryReporterWW = createTelemetryReporter(
            telemetryEndpoints.WW
        );
        this.perfTelemetryReporterEU = createTelemetryReporter(
            telemetryEndpoints.EU
        );

        this.getAllAccounts = getAllAccounts;
        this.getActiveAccount = getActiveAccount;
    }

    async initializeAppInsights(): Promise<void> {
        try {
            await Promise.all([
                this.perfTelemetryReporterWW.initializeAppInsights(),
                this.perfTelemetryReporterEU.initializeAppInsights(),
            ]);
        } catch (error) {
            this.logger.error(
                `TelemetryReporter: Failed to initialize one or both AppInsights instances: ${error}`
            );
            throw error;
        }
    }

    getTelemetryCallback(): PerformanceCallbackFunction {
        this.logger.trace(
            "TelemetryReporter: Attaching callback to send custom metrics."
        );
        const telemetryCallback: PerformanceCallbackFunction = (
            events: PerformanceEvent[]
        ) => {
            events.forEach((event) => {
                /*
                 * Set proper event name for broker event such that instead of grouping all broker events under the same table
                 * they are placed in the corresponding tables based on the underlying API names
                 * e.g. acquireTokenByBroker -> (acquireTokenSilent | ssoSilent | acquireTokenRedirect | acquireTokenPopup)
                 */
                if (event.name === PerformanceEvents.AcquireTokenByBroker) {
                    event.name = event.brokerEventName || event.name;
                    event.isBroker = true;
                    delete event.brokerEventName;
                    this.logger.verbosePii(
                        `TelemetryReporter: Updated broker event ${JSON.stringify(
                            event
                        )}`,
                        event.correlationId
                    );
                }

                const dataBoundary = determineDataBoundary(
                    event,
                    this.getActiveAccount.bind(this),
                    this.getAllAccounts.bind(this),
                    this.logger
                );

                this.logger.trace(
                    `TelemetryReporter: Sending event: ${event.name} to ${dataBoundary} endpoint`,
                    event.correlationId
                );

                if (dataBoundary === "EU") {
                    this.perfTelemetryReporterEU.sendCustomEvent(event);
                } else {
                    this.perfTelemetryReporterWW.sendCustomEvent(event);
                }
            });
        };
        this.logger.verbose(
            "TelemetryReporter: Returning callback as telemetry emitted."
        );
        return telemetryCallback;
    }
}
